/**
 * Customer-list hashing for Meta Custom Audiences — done entirely in the browser
 * so raw guest PII (email/phone/name) never leaves the device. We normalize per
 * Meta's spec, then SHA-256 to hex. Only the hashes are uploaded.
 */

export type PiiField = 'EMAIL' | 'PHONE' | 'FN' | 'LN' | 'GEN';

async function sha256Hex(s: string): Promise<string> {
  const bytes = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Meta's normalization rules, applied before hashing. Empty → '' (skip). */
export function normalize(field: PiiField, raw: string): string {
  const v = (raw ?? '').trim();
  if (!v) return '';
  switch (field) {
    case 'EMAIL':
      return v.toLowerCase();
    case 'PHONE':
      // Digits only, drop leading zeros; keep country code if present (Meta spec).
      return v.replace(/[^0-9]/g, '').replace(/^0+/, '');
    case 'FN':
    case 'LN':
      // Lowercase, strip punctuation, collapse spaces.
      return v
        .toLowerCase()
        .replace(/[^\p{L}\s'-]/gu, '')
        .replace(/\s+/g, ' ')
        .trim();
    case 'GEN': {
      const c = v[0]?.toLowerCase();
      return c === 'm' ? 'm' : c === 'f' ? 'f' : '';
    }
    default:
      return v;
  }
}

/** Hash one normalized value, or '' when the source cell is empty. */
export async function hashField(field: PiiField, raw: string): Promise<string> {
  const norm = normalize(field, raw);
  return norm ? sha256Hex(norm) : '';
}

/**
 * Minimal RFC-4180-ish CSV parser (handles quoted fields + escaped quotes).
 * Returns every non-empty row including the header.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}
